import { TypeChecker } from '@attain-apps/type-guards'

import type { DocumentNumber } from '../document-number'
import { ExpenseCategory } from '../expense-category'
import type { DocumentLookup } from '../justifications/document-lookup'
import * as payment from '../payment'
import type { ProjectId } from '../project-id'

import { Detail } from './detail'
import { Text } from './text'

/**
 * One row of the BCH `New Justifications` tab: everything known about a single
 * document's draw, and the justification text defending it.
 *
 * The tab is the outline's reviewable deliverable, so a row carries not just
 * the figures but why it does or does not need a human — the variance against
 * the drawdown, what was resolved automatically, and whether the award has
 * expired.
 */
export interface Row {
  readonly key: payment.Identifier
  readonly documentNumber: DocumentNumber
  /** Projects rolled into this document, sorted for stable output. */
  readonly projectIds: readonly ProjectId[]
  /**
   * Amounts by sponsor-facing category label, including
   * {@link ExpenseCategory.INDIRECT_COSTS}.
   */
  readonly amountsByCategory: Readonly<Record<string, number>>
  /** `Total Costs` — the sum of every category, and the amount requested. */
  readonly totalCosts: number
  /** `TOTAL DRAW AMOUNT` from the draw file's `PMS Download` tab. */
  readonly drawAmount?: number
  /** `Variance` — the draw amount less the total costs. */
  readonly variance?: number
  readonly awardEndDate?: string
  readonly expired: boolean
  readonly resolution: Row.Resolution
  /** Why this row needs a human; empty when it does not. */
  readonly reviewReasons: readonly string[]
  /** Absent when the document has nothing to request. */
  readonly text?: string
}
export namespace Row {
  /**
   * What was done about the variance between the draw amount and total costs.
   *
   * The outline resolves small and explainable variances automatically and
   * escalates the rest, so the row records which happened: a reviewer has to be
   * able to see that a figure was adjusted, not just that it balances.
   */
  export type Resolution =
    /** The draw already matched the total costs. */
    | { readonly kind: 'balanced' }
    /**
     * No draw amount, or a draw of zero. The outline leaves these alone —
     * usually projects that have not started.
     */
    | { readonly kind: 'noDraw' }
    /** A variance under a dollar, absorbed into indirect costs. */
    | { readonly kind: 'pluggedToIndirect'; readonly amount: number }
    /** The variance was exactly one category, which was zeroed out. */
    | {
        readonly kind: 'zeroedCategory'
        readonly category: string
        readonly amount: number
      }
    /** Too large to explain; a human has to decide. */
    | { readonly kind: 'unresolved' }

  export namespace Resolution {
    export const isResolution: TypeChecker<Resolution> =
      TypeChecker.Factory.union(
        TypeChecker.Factory.object({
          kind: TypeChecker.Factory.oneOf(['balanced'] as const),
        }),
        TypeChecker.Factory.object({
          kind: TypeChecker.Factory.oneOf(['noDraw'] as const),
        }),
        TypeChecker.Factory.object({
          amount: TypeChecker.isNumber,
          kind: TypeChecker.Factory.oneOf(['pluggedToIndirect'] as const),
        }),
        TypeChecker.Factory.object({
          amount: TypeChecker.isNumber,
          category: TypeChecker.isString,
          kind: TypeChecker.Factory.oneOf(['zeroedCategory'] as const),
        }),
        TypeChecker.Factory.object({
          kind: TypeChecker.Factory.oneOf(['unresolved'] as const),
        }),
      )

    /** A short phrase for the workbook's `Resolution` column. */
    export function describe(resolution: Resolution): string {
      switch (resolution.kind) {
        case 'balanced':
          return ''
        case 'noDraw':
          return 'No draw'
        case 'pluggedToIndirect':
          return `Variance of ${Text.formatCategory(
            resolution.amount,
          )} plugged to indirect costs`
        case 'zeroedCategory':
          return `${resolution.category} of ${Text.formatCategory(
            resolution.amount,
          )} zeroed to clear the variance`
        case 'unresolved':
          return 'Unresolved variance'
      }
    }
  }

  export const isRow: TypeChecker<Row> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object({
      amountsByCategory: TypeChecker.Factory.record(
        TypeChecker.isString,
        TypeChecker.isNumber,
      ),
      awardEndDate: TypeChecker.Factory.optional(TypeChecker.isString),
      documentNumber: TypeChecker.isString,
      drawAmount: TypeChecker.Factory.optional(TypeChecker.isNumber),
      expired: TypeChecker.isBoolean,
      key: TypeChecker.isString,
      projectIds: TypeChecker.Factory.list(TypeChecker.isString),
      resolution: Resolution.isResolution,
      reviewReasons: TypeChecker.Factory.list(TypeChecker.isString),
      text: TypeChecker.Factory.optional(TypeChecker.isString),
      totalCosts: TypeChecker.isNumber,
      variance: TypeChecker.Factory.optional(TypeChecker.isNumber),
    }),
  )

  /** Whether a row is held back for the outline's human review step. */
  export function needsReview(row: Readonly<Row>): boolean {
    return row.reviewReasons.length > 0
  }

  /**
   * Builds one row per document, in document order.
   *
   * Details arrive per project because that is the grain the 53A reports and
   * the overhead calculation work at, while the draw and the justification are
   * per document, so this is where the two meet.
   */
  export function from(
    details: readonly Detail[],
    documentLookup: DocumentLookup,
    period: Readonly<payment.Period>,
  ): readonly Row[] {
    const detailsByKey = Detail.byKey(details)

    return Object.values(documentLookup)
      .sort((left, right) => left.key.localeCompare(right.key))
      .map(entry =>
        _forDocument(entry, detailsByKey.get(entry.key) ?? [], period),
      )
  }

  /**
   * Variances at or above this are escalated rather than absorbed. The outline
   * says "for the variances under $1, we can plug in FnA".
   */
  const PLUG_THRESHOLD = 1

  /** Builds the row for one document. */
  function _forDocument(
    entry: DocumentLookup.Entry,
    details: readonly Detail[],
    period: Readonly<payment.Period>,
  ): Row {
    const drawAmount = entry.drawdownAmount
    const expired = payment.Period.isExpired(period, entry.awardEndDate)
    const resolved = _resolveVariance(_amountsByCategory(details), drawAmount)

    const totalCosts = Text.roundCurrency(
      Object.values(resolved.amountsByCategory).reduce(
        (sum, amount) => sum + amount,
        0,
      ),
    )
    /**
     * Expired awards get the same language as the rest. The outline asks for
     * "the same justification" and has the reviewer change its dates by hand,
     * which the expired flag and review reason point them to.
     */
    const text = Text.from({
      amountsByCategory: resolved.amountsByCategory,
      period,
      totalCosts,
    })

    return {
      amountsByCategory: resolved.amountsByCategory,
      awardEndDate: entry.awardEndDate,
      documentNumber: entry.documentNumber,
      drawAmount,
      expired,
      key: entry.key,
      projectIds: entry.projectIds,
      resolution: resolved.resolution,
      reviewReasons: _reviewReasons({
        expired,
        resolution: resolved.resolution,
        text,
      }),
      text,
      totalCosts,
      variance:
        drawAmount === undefined
          ? undefined
          : Text.roundCurrency(drawAmount - totalCosts),
    }
  }

  /**
   * Rolls a document's project rows up into sponsor-facing categories, with
   * calculated overhead as the twentieth.
   *
   * Every category is present, zero included, so the workbook's columns line up
   * whatever a given document happened to spend on.
   */
  function _amountsByCategory(
    details: readonly Detail[],
  ): Record<string, number> {
    const amounts: Record<string, number> = Object.fromEntries(
      ExpenseCategory.DISPLAY_ORDER.map(display => [display, 0]),
    )

    for (const [category, amount] of Object.entries(
      Detail.totalsByCategory(details),
    )) {
      const display = ExpenseCategory.toDisplay(category)
      amounts[display] = (amounts[display] ?? 0) + amount
    }

    /**
     * Overhead is summed from the per-project figures rather than recalculated,
     * because each was already rounded to cents — which is what the client's
     * pivot adds up. Rounding once at the end would drift from their workbook.
     */
    amounts[ExpenseCategory.INDIRECT_COSTS] = details.reduce(
      (total, detail) => total + detail.overhead,
      0,
    )
    return amounts
  }

  /**
   * Applies the outline's variance rules, returning the amounts to justify.
   *
   * The rules run in order of how much they assume: no draw at all is left
   * alone, a sub-dollar difference is absorbed into indirect costs, a
   * difference that is exactly one category is that category being wrong, and
   * anything else is a judgement call.
   */
  function _resolveVariance(
    amountsByCategory: Readonly<Record<string, number>>,
    drawAmount: number | undefined,
  ): {
    readonly amountsByCategory: Record<string, number>
    readonly resolution: Resolution
  } {
    const amounts = { ...amountsByCategory }

    if (drawAmount === undefined || Text.roundCurrency(drawAmount) === 0) {
      return { amountsByCategory: amounts, resolution: { kind: 'noDraw' } }
    }

    const totalCosts = Text.roundCurrency(
      Object.values(amounts).reduce((sum, amount) => sum + amount, 0),
    )
    const variance = Text.roundCurrency(drawAmount - totalCosts)
    if (variance === 0) {
      return { amountsByCategory: amounts, resolution: { kind: 'balanced' } }
    }

    if (Math.abs(variance) < PLUG_THRESHOLD) {
      amounts[ExpenseCategory.INDIRECT_COSTS] =
        (amounts[ExpenseCategory.INDIRECT_COSTS] ?? 0) + variance
      return {
        amountsByCategory: amounts,
        resolution: { amount: variance, kind: 'pluggedToIndirect' },
      }
    }

    /**
     * Only an over-claim can be explained this way: the total exceeds the draw
     * by exactly what one category holds, so that category does not belong.
     */
    const excess = Text.roundCurrency(-variance)
    const zeroable = excess > 0 ? _categoryWorth(amounts, excess) : undefined
    if (zeroable !== undefined) {
      amounts[zeroable] = 0
      return {
        amountsByCategory: amounts,
        resolution: {
          amount: excess,
          category: zeroable,
          kind: 'zeroedCategory',
        },
      }
    }

    return { amountsByCategory: amounts, resolution: { kind: 'unresolved' } }
  }

  /**
   * The first category, in worksheet order, holding exactly `amount`.
   *
   * Worksheet order rather than any match, so the same workbook always resolves
   * the same way when two categories happen to hold the same figure.
   */
  function _categoryWorth(
    amounts: Readonly<Record<string, number>>,
    amount: number,
  ): string | undefined {
    return Object.entries(amounts)
      .filter(([, value]) => Text.roundCurrency(value) === amount)
      .map(([label]) => label)
      .sort(
        (left, right) =>
          ExpenseCategory.displayOrderIndex(left) -
            ExpenseCategory.displayOrderIndex(right) ||
          left.localeCompare(right),
      )[0]
  }

  /** Everything about a row that a human has to look at. */
  function _reviewReasons(params: {
    readonly expired: boolean
    readonly resolution: Resolution
    readonly text: string | undefined
  }): string[] {
    const reasons: string[] = []
    if (params.expired) {
      reasons.push(
        'Award end date has passed; update the dates in the justification by hand.',
      )
    }
    if (params.resolution.kind === 'unresolved') {
      reasons.push('Draw amount does not match total costs.')
    }
    if (params.text !== undefined && params.text.length > Text.MAX_LENGTH) {
      reasons.push(
        `Justification is ${params.text.length} characters; PMS accepts ${Text.MAX_LENGTH}.`,
      )
    }
    return reasons
  }
}
